import { createRouter, createWebHistory } from "vue-router";
import ShowError from "@/components/ShowError.vue";
import StripeOauth from "@/components/StripeOauth.vue";
import StripeLink from "@/components/StripeLink.vue";
import store from "@/store";
import { getUriseOnlyRedirect } from "@/utils/uriseAccess";

let routes = [
  {
    path: "/",
    name: "Home",
    component: () => import("@/components/Home.vue"),
    meta: {
      requiresLogout: true,
      title: "Home | Uwill"
    }
  },
  {
    path: "/sso/:org_path",
    name: "HomeSaml",
    component: () => import("@/components/HomeSaml.vue"),
    meta: {
      requiresLogout: true,
      title: "Home | Uwill"
    }
  },
  {
    path: "/2fa",
    name: "TwoFactor",
    component: () => import("@/components/TwoFactor.vue"),
    meta: {
      title: "Set up Two Factor Authentication | Uwill",
      noindex: true,
    }
  },
  {
    path: "/mfa",
    name: "MultiFactorAuthentication",
    component: () => import("@/components/MultifactorAuthentication.vue"),
    meta: {
      title: "Device Authentication | Uwill",
      mfa: true,
      noindex: true,
    }
  },
  //route view
  {
    path: "/view/:target",
    name: "Permalink",
    component: () => import("@/components/redirect/Permalink.vue"),
    meta: {
      title: "Loading... | Uwill",
      noindex: true,
    }
  },
  {
    path: "/confirmaccountdeletion",
    name: "UserConfirmation",
    component: () => import("@/components/redirect/UserConfirmation.vue"),
    meta: {
      title: "Loading... | Uwill"
    }
  },

  {
    path: "/patient_transfer",
    name: "PatientTransfer",
    component: () => import("@/components/PatientTransfer.vue"),
    meta: {
      requiresAuth: false,
      title: "Patient Transfer | Uwill",
      noindex: true,
    },
    props: route => ({ tab: route.query.tab })
  },
  {
    path: "/dashboard",
    name: "UserDashboard",
    component: () => import("@/components/user/UserLayout.vue"),
    meta: {
      requiresAuth: true,
      title: "Dashboard | Uwill",
      noindex: true,
    },
    props: route => ({ tab: route.query.tab })
  },
  {
    path: "/collaborate",
    name: "Collaborate",
    component: () => import("@/components/Collaborate.vue"),
    meta: {
      requiresOrganization: true,
      title: "Organization Dashboard | Uwill",
      noindex: true
    },
    props: route => ({ tab: route.query.tab })
  },
  {
    path: "/crisis",
    name: "Operator",
    component: () => import("@/components/uhelp/OperatorDashboard.vue"),
    meta: {
      requiresOperator: true,
      title: "Crisis Worker Dashboard | Uwill",
      noindex: true,
    }
  },
  {
    path: "/appointment/:id",
    name: "Appointment",
    component: () => import("@/components/Appointment.vue"),
    meta: {
      requiresAuth: true,
      title: "Appointment Information | Uwill",
      noindex: true,
    }
  },
  {
    path: "/proposal/:token/:proposal_action",
    name: "AppointmentProposal",
    component: () => import("@/components/user/ProposalConfirmation.vue"),
    meta: {
      requiresAuth: true,
      title: "Appointment Proposal | Uwill",
      noindex: true,
    }
  },
  {
    path: "/rejoin/:token/:reinvitation_action",
    name: "Reinvitation",
    component: () => import("@/components/Reinvitation.vue"),
    meta: {
      requiresAuth: true,
      title: "Re-Join Organization | Uwill",
      noindex: true,
    }
  },
  {
    path: "/createappointment/:id",
    name: "CreateAppointment",
    component: () => import("@/components/CreateAppointment.vue"),
    meta: {
      requiresAuth: true,
      title: "Create an Appointment | Uwill",
      noindex: true,
    }
  },
  {
    path: "/update-appointment/:id",
    name: "UpdateAppointment",
    component: () => import("@/components/UpdateAppointment.vue"),
    meta: {
      requiresAuth: true,
      title: "Update your Appointment | Uwill",
      noindex: true,
    }
  },
  {
    path: "/findtherapist",
    name: "FindProvider",
    component: () => import("@/components/providersearch/FindProvider.vue"),
    meta: {
      requiresAuth: true,
      noindex: true,
    }
  },
  {
    path: "/testchat",
    name: "ZoomChatPage",
    component: () => import("@/components/ZoomChatPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Zoom Chat Page | Uwill",
      noindex: true,
    }
  },
  {
    path: "/purchasecredits",
    name: "PurchaseCredits",
    component: () => import("@/components/PurchaseCredits.vue"),
    meta: {
      requiresAuth: true,
      title: "Purchase Credits | Uwill",
      noindex: true,
    }
  },
  {
    path: "/consent",
    name: "ConsentToTreat",
    component: () => import("@/components/ConsentToTreat.vue"),
    meta: {
      requiresMissingConsent: true,
      title: "Consent to Treat | Uwill",
      noindex: true,
    }
  },
  {
    path: "/guardianconsent/:guardian/:minor",
    name: "GuardianConsent",
    component: () => import("@/components/GuardianConsent.vue"),
    meta: {
      title: "Guardian Consent | Uwill",
      noindex: true,
    }
  },
  {
    path: "/ltilaunch",
    name: "LtiLaunch",
    component: () => import("@/components/LtiLaunch.vue"),
    meta: {
      title: "Redirecting to Uwill",
      noindex: true,
    }
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/components/Login.vue"),
    meta: {
      requiresLogout: true,
      title: "Login | Uwill"
    }
  },
  {
    path: "/register/:token?",
    name: "Register",
    component: () => import("@/components/Register.vue"),
    meta: {
      requiresLogout: true,
      title: "Register to Join | Uwill",
      noindex: true
    }
  },
  {
    path: "/confirm/:token",
    name: "ConfirmEmail",
    component: () => import("@/components/ConfirmEmail.vue"),
    meta: {
      title: "Confirm Your Email Address | Uwill",
      noindex: true,
    }
  },
  {
    path: "/engagementoptout/:token",
    name: "EngagementOptOut",
    component: () => import("@/components/EngagementOptOut.vue")
  },
  {
    path: "/feedback/:id",
    name: "FeedbackWrapper",
    component: () => import("@/components/FeedbackWrapper.vue"),
    meta: {
      requiresAuth: true,
      title: "Appointment Feedback | Uwill",
      noindex: true,
    }
  },
  {
    path: "/forgotpassword",
    name: "ForgotPassword",
    component: () => import("@/components/ForgotPassword.vue"),
    meta: {
      title: "Forgot Your Password | Uwill",
      noindex: true,
    }
  },
  {
    path: "/events/:slug",
    name: "EventRegistration",
    component: () => import("@/components/EventRegistration.vue"),
    meta: {
      title: "Event Registration | Uwill",
      noindex: true,
    }
  },
  {
    path: "/talknow/:token",
    name: "TalkNow",
    component: () => import("@/components/TalkNow.vue"),
    meta: {
      title: "Talk Now | Uwill",
      noindex: true,
    }
  },
  {
    path: "/talknow",
    name: "CreateTalkNow",
    component: () => import("@/components/CreateTalkNow.vue"),
    meta: {
      title: "Talk Now | Uwill",
      noindex: true,
    }
  },
  {
    path: "/activity",
    name: "Activity",
    component: () => import("@/components/Activity.vue"),
    meta: {
      requiresAuth: true,
      title: "Activity | Uwill",
      noindex: true,
    }
  },
  {
    path: "/availability",
    redirect: () => {
      return { path: "/dashboard", query: { tab: "availability" } };
    }
  },
  {
    path: "/gcal:afterGcal(.*)",
    name: "GoogleCalendar",
    component: () => import("@/components/GoogleCalendar.vue"),
    meta: {
      requiresAuth: true,
      title: "Integrate your Calendar | Uwill",
      noindex: true,
    }
  },
  {
    path: "/history",
    name: "history",
    component: () => import("@/components/provider/PatientHistory.vue"),
    meta: {
      requiresAuth: true,
      title: "History | Uwill",
      noindex: true,
    }
  },
  {
    path: "/changeover",
    name: "changeover",
    component: () => import("@/components/ChangeOver.vue"),
    meta: {
      requiresAuth: true,
      title: "Account Changeover | Uwill",
      noindex: true,
    }
  },
  {
    path: "/files",
    name: "files",
    component: () => import("@/components/Files.vue"),
    meta: {
      requiresAuth: true,
      title: "Files | Uwill",
      noindex: true,
    }
  },
  {
    path: "/resetpassword/:token",
    name: "ResetPassword",
    component: () => import("@/components/ResetPassword.vue"),
    meta: {
      title: "Reset Your Password | Uwill",
      noindex: true,
    }
  },
  {
    path: "/connect/oauth",
    name: "StripeOauth",
    component: StripeOauth
  },
  {
    path: "/connect/link",
    name: "StripeLink",
    component: StripeLink
  },
  {
    path: "/signon/:token/:otp",
    name: "GlobalSignOn",
    component: () => import("@/components/GlobalSignOn.vue"),
    meta: {
      requiresLogout: true,
      title: "Login | Uwill",
      noindex: true,
    }
  },
  {
    path: "/demoauth",
    name: "Demo",
    component: () => import("@/components/Demo.vue"),
    meta: {
      title: "Demo | Uwill",
      noindex: true,
    }
  },
  {
    path: "/logout",
    name: "logout"
  },
  {
    path: "/error",
    name: "ShowError",
    component: ShowError
  },
  {
    path: "/doc/:type",
    name: "Documentation",
    component: () => import("@/components/Documentation.vue")
  },
  {
    // For Student to take a new Self-Assessment
    path: "/assessment",
    name: "SelfAssessment",
    component: () => import("@/components/outcomes/SelfAssessment.vue")
  },
  {
    path: "/viewprovider",
    name: "SingleProviderView",
    component: () => import("@/components/providersearch/SingleProviderView.vue"),
    meta: {
      requiresAuth: true,
      title: "Single Provider View",
      noindex: true,
    },
  },

  // For Vue 3, the * path has been split into /:pathMatch(.*)* and /:pathMatch(.*)
  // They're essentially the same, so use both
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/components/Home.vue")
  },
  {
    path: "/:pathMatch(.*)",
    component: () => import("@/components/Home.vue")
  }
];

const host = window.location.host;
if (
  process.env.VUE_APP_LANDING_DOMAIN &&
  host.indexOf(process.env.VUE_APP_LANDING_DOMAIN) != -1
) {
  routes = [
    {
      path: "/",
      name: "Landing",
      component: () => import("@/components/Landing.vue")
    }
  ];
}

const router = createRouter({
  history: createWebHistory(),
  base: process.env.BASE_URL,
  routes
});

router.beforeEach((to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title);
  if (nearestWithTitle) document.title = nearestWithTitle.meta.title;

  if (
    (process.env.NODE_ENV === "production" ||
      process.env.NODE_ENV === "staging" ||
      process.env.NODE_ENV === "demo" ||
      process.env.NODE_ENV === "qa") &&
    location.protocol !== "https:"
  ) {
    location.replace(
      `https:${location.href.substring(location.protocol.length)}`
    );
  }

  if (process.env.NODE_ENV === "demo" && to.name != "Demo") {
    // Check Auth
    let auth = localStorage.getItem("uwill_demo_authorized");
    var demo_auth_redirect = true;
    if (auth) {
      let now = new Date();
      let permitted = Date.parse(auth) > now.valueOf();
      if (permitted) {
        demo_auth_redirect = false;
      } else {
        demo_auth_redirect = true;
        localStorage.removeItem("uwill_demo_authorized");
      }
    }
    if (demo_auth_redirect) {
      next("/demoauth");
    }
  }

  if (to.name == "logout") {
    store.dispatch("logout").then(() => {
      next("/login");
    });
  } else if (
    store.getters.mfaRequired &&
    !to.matched.some(record => record.meta.mfa)
  ) {
    next("/mfa");
  } else if (store.getters.isLoggedIn && store.getters.isUriseOnly) {
    const redirect = getUriseOnlyRedirect(to, store.getters.needsTFA);
    next(redirect || undefined);
  } else if (
    store.getters.isChangeover &&
    to.name != "changeover" &&
    store.getters.isConfirmed
  ) {
    next("/changeover");
  } else if (
    store.getters.isOperator &&
    !to.matched.some(record => record.meta.requiresOperator)
  ) {
    if (to.name == "TwoFactor" || store.getters.needsTFA) {
      next();
    } else {
      next("/crisis");
    }
  } else if (to.matched.some(record => record.meta.requiresAuth)) {
    if (store.getters.needsTFA) {
      next("/2fa");
    } else if (store.getters.isLoggedIn) {
      next();
    } else if (!store.getters.isLoggedIn) {
      next({
        name: 'Login',
        query: { redirect: to.fullPath } // save the original path
      });
    } else {
      next("/login");
    }
  } else if (to.matched.some(record => record.meta.requiresLogout)) {
    if (store.getters.isLoggedIn) {
      if(store.getters.isCollaborate){
        next("/collaborate");
      } else {

        // If user is signed in and this is SSO landing page, do not redirect to Dashboard
        // This has its own downstream redirect
        if (document.location.pathname.includes("/sso")) {
          next();
        } else {
          // If user is signed in and lands on Home, redirect to Dashboard
          next("/dashboard");
        }
      }
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.requiresMissingConsent)) {
    if (!store.getters.isLoggedIn) {
      next("/login");
    } else if (store.state.user.consentToTreat) {
      next("/dashboard");
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.demoInterstitial)) {
    next();
  } else {
    next();
  }
});

router.afterEach((to) => {
  // Remove existing <meta name="robots"> tag
  const existingMeta = document.querySelector("meta[name='robots']");
  if (existingMeta) {
    existingMeta.remove();
  }

  // Determine if this route should not be indexed
  const shouldNoindex =
    to.matched.some(r => r.meta && r.meta.noindex) ||
    (to.path === "/register" && to.query.classification === "provider")||
    to.path.startsWith("/collaborate");

  if (shouldNoindex) {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
  }
});


export default router;
